import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CompMap from '../components/CompMap';
import DealPacketModal from '../components/DealPacketModal';
import GenerateAssignmentModal from '../components/GenerateAssignmentModal';
import DealEditModal from '../components/DealEditModal';
import StageBadge from '../components/StageBadge';
import SoldCloseModal from '../components/SoldCloseModal';
import PitchBuyerModal from '../components/PitchBuyerModal';
import { useAuth } from '../contexts/AuthContext';
import { canSell } from '../utils/roles';
import { proxyPhoto } from '../utils/photo';

// Shared, SALES-FACING deal view reached from the Pipeline. Shows only the sales-safe fields —
// asking price, rehab estimate, close date, title company, comps, ARV, and a sanitized analysis.
// MAO / wholesale fee / front-side price are stripped server-side (see backend/lib/redaction.js), so
// there's nothing sensitive to render here even for a curious agent. The owner's full view is the
// Underwriting page, reached from their own Deals list.

function fmtMoney(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v >= 1000000) return `$${(v / 1000000).toFixed(2)}M`;
  if (v >= 1000)    return `$${Math.round(v / 1000)}k`;
  return `$${v}`;
}
function fmtDate(d) {
  if (!d) return '—';
  const s = String(d).split('T')[0].split(' ')[0];
  if (!s || s === 'null') return '—';
  const [y, m, day] = s.split('-');
  return (y && m && day) ? `${m}/${day}/${y}` : s;
}

function Fact({ label, value, accent }) {
  return (
    <div style={{ flex: 1, minWidth: 140, background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent || 'var(--white)' }}>{value}</div>
    </div>
  );
}
function Section({ title, right, children }) {
  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function PipelineDeal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [uw, setUw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [soldOpen, setSoldOpen] = useState(false);
  const [pitchOpen, setPitchOpen] = useState(false);
  const { role } = useAuth();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/deals/${id}`).then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { if (!cancelled) { setDeal(d); } })
      .catch(s => { if (!cancelled && s === 404) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Underwriting is best-effort — it carries comps/ARV/analysis but may 403 without the entitlement.
    fetch(`/api/underwriting/${id}`).then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setUw(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Re-pull just the deal after a stage move (owner/admin only; the backend gates the write).
  const refetchDeal = () => {
    fetch(`/api/deals/${id}`).then(r => r.ok ? r.json() : null).then(d => { if (d) setDeal(d); }).catch(() => {});
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Loading deal…</div>;
  if (notFound || !deal) return <div style={{ padding: 40, color: 'var(--gray-500)' }}>Deal not found.</div>;

  const photos = Array.isArray(deal.photos) ? deal.photos : (typeof deal.photos === 'string' ? safeArr(deal.photos) : []);
  const hero = deal.photo_url || photos[0] || null;
  const arv = uw?.arvAnalysis?.estimate ?? deal.ai_arv;
  const rehab = uw?.repairAnalysis?.total ?? deal.repair_cost;
  const closeDisplay = fmtDate(deal.close_date || deal.close_of_escrow);
  const analysis = uw?.aiNarrative || deal.description || null;

  // Owner / org admin / deal owner (server sets financials_redacted=false for them) can edit the deal
  // right from the Pipeline and see the closing details on Sold/Closed deals.
  const canManage = !deal.financials_redacted;
  const isSoldClosed = ['Sold', 'Closed'].includes(deal.stage);
  // Who sold it: the account recorded by the sale (dispo_user_id) first. The free-text field only for a
  // sheet/import closing with no account behind it, and never the acquisitions agent — a different
  // person, which is what the old fallback printed under "Sales Agent".
  const salesAgent = deal.dispo_user_name || deal.dispositions_agent || null;
  const feeAmount = deal.fee ?? deal.assignment_fee;
  // The sales side works an Available deal from here without owning it: pitch it, draft the
  // assignment, mark it Sold. Once Sold, only its managers (now including whoever sold it) may.
  // The server decides all three; this only decides what to draw.
  const onBoard = deal.stage === 'Available';
  const mayWorkSale = canManage || (onBoard && canSell(role));

  return (
    <div style={{ maxWidth: 920 }}>
      {/* Back + context banner */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/pipeline')} style={{ padding: '6px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>← Pipeline</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {canManage && (
            <button onClick={() => setEditOpen(true)} style={{ padding: '6px 12px', borderRadius: 8, background: 'transparent', border: '1px solid rgba(74,124,89,0.5)', color: '#4A7C59', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              title="Edit deal metrics — available to the deal owner or an org admin">✎ Edit Deal</button>
          )}
          <button onClick={() => setPacketOpen(true)} style={{ padding: '6px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📄 Deal Packet</button>
          {onBoard && canSell(role) && (
            <button onClick={() => setPitchOpen(true)} style={{ padding: '6px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--teal)', color: 'var(--teal)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✉ Pitch to Buyer</button>
          )}
          {mayWorkSale && (
            <button onClick={() => setAssignOpen(true)} style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📑 Generate Assignment</button>
          )}
          {onBoard && canSell(role) && (
            <button onClick={() => setSoldOpen(true)} style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--gold)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              title="Record the sale — you become this deal's sales agent">✓ Mark Sold</button>
          )}
          {!canManage && (
            <span style={{ fontSize: 11, background: 'rgba(74,124,89,0.1)', border: '1px solid rgba(74,124,89,0.3)', color: '#4A7C59', padding: '5px 10px', borderRadius: 20, fontWeight: 700 }}>
              Shared pipeline view — owner financials hidden
            </span>
          )}
        </div>
      </div>

      {packetOpen && <DealPacketModal dealId={id} dealAddress={deal.address} onClose={() => setPacketOpen(false)} />}
      {assignOpen && <GenerateAssignmentModal deal={deal} onClose={() => setAssignOpen(false)} />}
      {editOpen && <DealEditModal deal={deal} onClose={() => setEditOpen(false)} onSaved={refetchDeal} />}
      {pitchOpen && <PitchBuyerModal deal={deal} arv={arv} onClose={() => setPitchOpen(false)} />}
      {soldOpen && (
        <SoldCloseModal dealId={deal.id} stage="Sold" deal={deal} onClose={() => setSoldOpen(false)}
          onConfirmed={() => { setSoldOpen(false); refetchDeal(); }} />
      )}

      {/* Header */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start' }}>
        {hero ? (
          <img src={proxyPhoto(hero)} alt="" style={{ width: 160, height: 110, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--navy-border)', flexShrink: 0 }} />
        ) : null}
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.02em', marginBottom: 4 }}>{deal.address}</h1>
          <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 8 }}>{[deal.city, deal.state, deal.zip].filter(Boolean).join(', ')}{deal.mls_id ? ` · MLS ${deal.mls_id}` : ''}</div>
          <div style={{ fontSize: 13, color: 'var(--gray-300)', marginBottom: 10 }}>
            {[deal.beds && `${deal.beds} bd`, deal.baths && `${deal.baths} ba`, deal.sqft && `${Number(deal.sqft).toLocaleString()} sqft`, deal.year_built && `built ${deal.year_built}`].filter(Boolean).join(' · ') || '—'}
          </div>
          {/* Owner / org admin can move the deal through stages right from the Pipeline; agents see a
              read-only pill (the backend also gates the write to owner/admin). */}
          <StageBadge stage={deal.stage} dealId={deal.id} deal={deal}
            readOnly={!!deal.financials_redacted} onChanged={refetchDeal} />
        </div>
      </div>

      {/* Key sales-safe facts */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <Fact label="Asking Price" value={fmtMoney(deal.asking_price)} />
        <Fact label="Est. ARV" value={fmtMoney(arv)} accent="#4A7C59" />
        <Fact label="Rehab Estimate" value={fmtMoney(rehab)} accent="var(--gold)" />
        <Fact label="Close Date" value={closeDisplay} />
        <Fact label="Title Company" value={deal.title_company || '—'} />
      </div>

      {/* Closing details — visible on Sold/Closed deals to the deal owner / org admin. Carries the
          fee, sales agent, end buyer and notes captured at close (financials are redacted for agents). */}
      {isSoldClosed && canManage && (
        <Section title="Closing Details">
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: (deal.description ? 14 : 0) }}>
            {[
              ['Sold Price', fmtMoney(deal.sale_price), 'var(--white)'],
              ['Assignment Fee', fmtMoney(feeAmount), 'var(--gold)'],
              ['Sales Agent', salesAgent || '—', 'var(--white)'],
              ['End Buyer', deal.buyer_name || '—', 'var(--white)'],
              ['Close Date', closeDisplay, 'var(--white)'],
            ].map(([k, v, c]) => (
              <div key={k} style={{ minWidth: 120 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>
          {deal.description && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 14, color: 'var(--gray-300)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{deal.description}</div>
            </div>
          )}
        </Section>
      )}

      {/* ARV detail */}
      {uw?.arvAnalysis && (
        <Section title="ARV Analysis" right={uw.arvSourceLabel ? <span style={{ fontSize: 10, color: 'var(--teal)', fontWeight: 600 }}>{uw.arvSourceLabel}</span> : null}>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[['Estimate', fmtMoney(uw.arvAnalysis.estimate)], ['Low', fmtMoney(uw.arvAnalysis.low)], ['High', fmtMoney(uw.arvAnalysis.high)], ['$/sqft', uw.arvAnalysis.perSqft ? `$${uw.arvAnalysis.perSqft}` : '—'], ['Confidence', uw.arvAnalysis.confidence]].map(([k, v]) => (
              <div key={k}><div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{k}</div><div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>{v}</div></div>
            ))}
          </div>
        </Section>
      )}

      {/* Comps + map */}
      {uw?.comps && uw.comps.length > 0 && (
        <Section title="Comparable Properties">
          <div style={{ marginBottom: 12 }}>
            <CompMap subject={{ address: deal.address, lat: deal.lat, lng: deal.lng, sqft: deal.sqft }} comps={uw.comps} height={240} />
          </div>
          {(uw.compWarnings || []).map((w, i) => (
            <div key={i} style={{ padding: '6px 10px', background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 6, fontSize: 11, color: 'var(--gold)', marginBottom: 8 }}>{w}</div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
            {uw.comps.slice(0, 6).map((c, i) => (
              <div key={c.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(232,227,218,0.5)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.address}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{[c.beds && `${c.beds}bd`, c.baths && `${c.baths}ba`, c.sqft && `${Number(c.sqft).toLocaleString()} sqft`].filter(Boolean).join(' / ')}{c.distance != null ? ` · ${Number(c.distance).toFixed(1)}mi` : ''}</div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--white)', fontWeight: 700, whiteSpace: 'nowrap', marginLeft: 12 }}>{fmtMoney(c.close_price || c.price || c.sale_price)}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Analysis (sanitized — no offer/fee/profit) */}
      {analysis && (
        <Section title="Analysis">
          <div style={{ fontSize: 14, color: 'var(--gray-200, var(--gray-300))', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{analysis}</div>
        </Section>
      )}
    </div>
  );
}

function safeArr(s) { try { const a = JSON.parse(s); return Array.isArray(a) ? a : []; } catch { return []; } }
